// Pure, reusable field registry for the bulk "AI Calibre Enhancement" tool on
// the Curated Calibres page (BulkCalibreAIEnhance).
//
// This is the calibre analogue of bulkEnrichFields.ts (which targets watch
// *references*). Where that module maps suggestion keys → ref dotted-paths, this
// one maps the AI `specs` keys returned by the `enrichCalibre` callable onto the
// custom-calibre document's `data.<field>` (CalibreData) sub-map, plus the
// top-level `description`.
//
// The apply step deliberately reuses `aiSpecsToCalibreData` (the same coercion
// the single-calibre AI panel saves through) so a bulk write is indistinguishable
// from accepting the suggestion in the per-calibre editor — including the few
// spec→data key remaps (`diameter`→`diameterMm`, `height`→`heightMm`).
//
// Dotted-path keys (e.g. 'data.jewels') let updateDoc() merge a single nested
// field without clobbering its CalibreData siblings.

import { aiSpecsToCalibreData } from '../components/CuratedWatches/AiCalibreEnrichPanel';
import type { CalibreAliasDetail, CalibreData, CustomCalibreEntry } from './firebase';

export type CalibreFieldType = 'str' | 'num' | 'bool' | 'arr';

export interface CalibreEnrichFieldSpec {
  /** Key as it appears in the AI `specs` object. */
  specKey: string;
  /** Key on CalibreData (the `data.<dataKey>` Firestore path). */
  dataKey: keyof CalibreData & string;
  /** Human label — column header in the review grid. */
  label: string;
  type: CalibreFieldType;
}

// Order here is the column order in the review grid. Mirrors the spec→CalibreData
// mapping in aiSpecsToCalibreData so every field the AI can return has a column.
const SPEC_LIST: CalibreEnrichFieldSpec[] = [
  { specKey: 'movementType', dataKey: 'movementType', label: 'Movement type', type: 'str' },
  { specKey: 'baseMovement', dataKey: 'baseMovement', label: 'Base movement', type: 'str' },
  { specKey: 'jewels', dataKey: 'jewels', label: 'Jewels', type: 'num' },
  { specKey: 'powerReserveHours', dataKey: 'powerReserveHours', label: 'Power reserve (h)', type: 'num' },
  { specKey: 'frequencyVph', dataKey: 'frequencyVph', label: 'Frequency (vph)', type: 'num' },
  { specKey: 'diameter', dataKey: 'diameterMm', label: 'Diameter (mm)', type: 'num' },
  { specKey: 'height', dataKey: 'heightMm', label: 'Height (mm)', type: 'num' },
  { specKey: 'liftAngleDegrees', dataKey: 'liftAngleDegrees', label: 'Lift angle (°)', type: 'num' },
  { specKey: 'serviceIntervalYears', dataKey: 'serviceIntervalYears', label: 'Service interval (yr)', type: 'num' },
  { specKey: 'casingDiameterMm', dataKey: 'casingDiameterMm', label: 'Casing diameter (mm)', type: 'num' },
  { specKey: 'lignes', dataKey: 'lignes', label: 'Lignes', type: 'str' },
  { specKey: 'windingDirection', dataKey: 'windingDirection', label: 'Winding direction', type: 'str' },
  { specKey: 'manualWindingDirection', dataKey: 'manualWindingDirection', label: 'Manual winding direction', type: 'str' },
  { specKey: 'hacking', dataKey: 'hacking', label: 'Hacking', type: 'bool' },
  { specKey: 'manualWinding', dataKey: 'manualWinding', label: 'Manual winding', type: 'bool' },
  { specKey: 'quickSetDate', dataKey: 'quickSetDate', label: 'Quick-set date', type: 'bool' },
  { specKey: 'isChronograph', dataKey: 'isChronograph', label: 'Is chronograph', type: 'bool' },
  { specKey: 'isGMT', dataKey: 'isGMT', label: 'Is GMT', type: 'bool' },
  { specKey: 'isChronometer', dataKey: 'isChronometer', label: 'Is chronometer', type: 'bool' },
  { specKey: 'isCosc', dataKey: 'isCosc', label: 'Is COSC', type: 'bool' },
  { specKey: 'isGlashuetteRegulation', dataKey: 'isGlashuetteRegulation', label: 'Glashütte reg.', type: 'bool' },
  { specKey: 'isGenevaSeal', dataKey: 'isGenevaSeal', label: 'Geneva Seal', type: 'bool' },
  { specKey: 'isGrandSeikoStandard', dataKey: 'isGrandSeikoStandard', label: 'GS Standard', type: 'bool' },
  { specKey: 'isMETAS', dataKey: 'isMETAS', label: 'METAS', type: 'bool' },
  { specKey: 'isInHouse', dataKey: 'isInHouse', label: 'In-house', type: 'bool' },
  { specKey: 'isFamily', dataKey: 'isFamily', label: 'Movement family', type: 'bool' },
  { specKey: 'columnWheel', dataKey: 'columnWheel', label: 'Column wheel', type: 'bool' },
  { specKey: 'doubleBarrel', dataKey: 'doubleBarrel', label: 'Double barrel', type: 'bool' },
  { specKey: 'antiShockSystem', dataKey: 'antiShockSystem', label: 'Anti-shock system', type: 'str' },
  { specKey: 'countryOfManufacture', dataKey: 'countryOfManufacture', label: 'Country of manufacture', type: 'str' },
  { specKey: 'escapement', dataKey: 'escapement', label: 'Escapement', type: 'str' },
  { specKey: 'regulatorSystem', dataKey: 'regulatorSystem', label: 'Regulator system', type: 'str' },
  { specKey: 'hairspring', dataKey: 'hairspring', label: 'Hairspring', type: 'str' },
  { specKey: 'balanceType', dataKey: 'balanceType', label: 'Balance type', type: 'arr' },
  { specKey: 'movementArchitecture', dataKey: 'movementArchitecture', label: 'Architecture', type: 'str' },
  { specKey: 'windingArchitecture', dataKey: 'windingArchitecture', label: 'Auto winding architecture', type: 'str' },
  { specKey: 'movementFinishing', dataKey: 'movementFinishing', label: 'Finishing', type: 'str' },
  { specKey: 'plateFinishing', dataKey: 'plateFinishing', label: 'Plate finishing', type: 'str' },
  { specKey: 'skeletonization', dataKey: 'skeletonization', label: 'Skeletonization', type: 'str' },
  { specKey: 'decorations', dataKey: 'decorations', label: 'Decorations', type: 'arr' },
  { specKey: 'rotorMaterial', dataKey: 'rotorMaterial', label: 'Rotor material', type: 'str' },
  { specKey: 'bridgeMaterial', dataKey: 'bridgeMaterial', label: 'Bridge material', type: 'str' },
  { specKey: 'caseMaterialsAvailable', dataKey: 'caseMaterialsAvailable', label: 'Case materials available', type: 'arr' },
  { specKey: 'rhodiumPlated', dataKey: 'rhodiumPlated', label: 'Rhodium plated', type: 'bool' },
  { specKey: 'giltFinish', dataKey: 'giltFinish', label: 'Gilt finish', type: 'bool' },
  { specKey: 'numberOfBridges', dataKey: 'numberOfBridges', label: 'Bridges', type: 'num' },
  { specKey: 'barrelCount', dataKey: 'barrelCount', label: 'Barrels', type: 'num' },
  { specKey: 'rotorType', dataKey: 'rotorType', label: 'Rotor type', type: 'str' },
  { specKey: 'batteryType', dataKey: 'batteryType', label: 'Battery type', type: 'str' },
  { specKey: 'batteryLifeYears', dataKey: 'batteryLifeYears', label: 'Battery life (yr)', type: 'num' },
  { specKey: 'shockResistanceG', dataKey: 'shockResistanceG', label: 'Shock resistance (g)', type: 'num' },
  { specKey: 'antiMagneticAm', dataKey: 'antiMagneticAm', label: 'Anti-magnetic (A/m)', type: 'num' },
  { specKey: 'antiMagneticGauss', dataKey: 'antiMagneticGauss', label: 'Anti-magnetic (gauss)', type: 'num' },
  { specKey: 'date', dataKey: 'date', label: 'Date', type: 'arr' },
  { specKey: 'hands', dataKey: 'hands', label: 'Hands', type: 'arr' },
  { specKey: 'chronograph', dataKey: 'chronograph', label: 'Chronograph', type: 'arr' },
  { specKey: 'astronomical', dataKey: 'astronomical', label: 'Astronomical', type: 'arr' },
  { specKey: 'acoustic', dataKey: 'acoustic', label: 'Acoustic', type: 'arr' },
  { specKey: 'additionals', dataKey: 'additionals', label: 'Additionals', type: 'arr' },
];

/** Pseudo-key for the canonical-name column (sourced from result.canonicalName,
 *  not result.specs, and persisted to the doc's top-level `name` field — with the
 *  previous name demoted into the AKA arrays on a rename). */
export const CALIBRE_NAME_KEY = '__name__';

/** Pseudo-key for the top-level `description` column (sourced from result.description,
 *  not result.specs, and persisted to the doc's top-level `description` field). */
export const CALIBRE_DESCRIPTION_KEY = '__description__';

/** The two "always-apply" fields. Unlike spec fields they ignore the fill/overwrite
 *  write-mode and default to checked even when the calibre already has a value:
 *  name → canonical rename (+ AKA demotion of the old name); description → full
 *  replace. Mirrors the single-calibre flow where these always apply. */
export const CALIBRE_ALWAYS_APPLY_KEYS = new Set<string>([CALIBRE_NAME_KEY, CALIBRE_DESCRIPTION_KEY]);

export const CALIBRE_ENRICH_FIELDS: Record<string, CalibreEnrichFieldSpec> = Object.fromEntries(
  SPEC_LIST.map((s) => [s.specKey, s]),
);

/** Column order in the review grid: canonical name first, then the spec fields,
 *  with the (long) description column last. */
export const CALIBRE_ENRICH_FIELD_ORDER: string[] = [
  CALIBRE_NAME_KEY,
  ...SPEC_LIST.map((s) => s.specKey),
  CALIBRE_DESCRIPTION_KEY,
];

export function getCalibreFieldSpec(specKey: string): CalibreEnrichFieldSpec | undefined {
  return CALIBRE_ENRICH_FIELDS[specKey];
}

export function getCalibreFieldLabel(specKey: string): string {
  if (specKey === CALIBRE_NAME_KEY) return 'Canonical name';
  if (specKey === CALIBRE_DESCRIPTION_KEY) return 'Description';
  return CALIBRE_ENRICH_FIELDS[specKey]?.label ?? specKey;
}

/** The calibre's current value for a field as a display string. '' means "no
 *  existing value". A stored boolean `false` counts as existing (so "fill empty
 *  only" won't overwrite it), mirroring bulkEnrichFields' dispBool. */
export function getCalibreExisting(entry: CustomCalibreEntry, specKey: string): string {
  if (specKey === CALIBRE_NAME_KEY) {
    return (entry.name || '').trim();
  }
  if (specKey === CALIBRE_DESCRIPTION_KEY) {
    return (entry.description || '').trim();
  }
  const spec = CALIBRE_ENRICH_FIELDS[specKey];
  if (!spec) return '';
  const v = (entry.data || {})[spec.dataKey] as unknown;
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean).join(', ');
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  return '';
}

/** The subset of an enrichment result this module reads from. */
export interface CalibreEnrichResultLike {
  specs: Record<string, unknown>;
  description?: string;
  /** AI-proposed canonical name (may equal the current name when no rename). */
  canonicalName?: string;
}

/** Read a suggested value for a field out of an enrichment result. Name comes from
 *  `canonicalName`, description from `description`; everything else from `specs`. */
export function getCalibreSuggestion(result: CalibreEnrichResultLike, specKey: string): unknown {
  if (specKey === CALIBRE_NAME_KEY) return result.canonicalName;
  if (specKey === CALIBRE_DESCRIPTION_KEY) return result.description;
  return result.specs[specKey];
}

/** True when the AI proposes a canonical name that differs (case-insensitively)
 *  from the calibre's current name — i.e. a real rename worth offering. */
export function isCalibreRename(entry: CustomCalibreEntry, canonicalName: string | undefined): boolean {
  const next = (canonicalName || '').trim();
  if (!next) return false;
  return next.toLowerCase() !== (entry.name || '').trim().toLowerCase();
}

/**
 * Build the Firestore patch for a set of accepted cells of a single calibre.
 *
 * - Spec cells → `data.<field>` paths, coerced via aiSpecsToCalibreData so the
 *   write matches exactly what the per-calibre AI panel persists.
 * - `description` cell → top-level `description`.
 * - `name` cell → top-level `name`, with the PREVIOUS name demoted into the AKA
 *   arrays (`alsoKnownAs` + `aliasDetails`), mirroring the single-calibre flow
 *   (CustomCalibreModal.applyAiFields). Both AKA arrays are kept in sync.
 *
 * Returns `{}` when nothing usable was accepted.
 */
export function buildCalibrePatch(
  entry: CustomCalibreEntry,
  acceptedSpecKeys: string[],
  result: CalibreEnrichResultLike,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  // Canonical name (always-apply). On a real rename, set the new name and move the
  // old one into the AKA arrays so references that still match the old string keep
  // matching. Skip silently when it's not actually a rename.
  if (acceptedSpecKeys.includes(CALIBRE_NAME_KEY) && isCalibreRename(entry, result.canonicalName)) {
    const newName = (result.canonicalName || '').trim();
    const oldName = (entry.name || '').trim();
    patch.name = newName;
    if (oldName) {
      const lowerOld = oldName.toLowerCase();
      const existingAliases = entry.alsoKnownAs || [];
      if (!existingAliases.some((a) => a.trim().toLowerCase() === lowerOld)) {
        patch.alsoKnownAs = [...existingAliases, oldName];
      }
      const existingDetails = entry.aliasDetails || [];
      if (!existingDetails.some((d) => (d.name || '').trim().toLowerCase() === lowerOld)) {
        // Scope the demoted alias to the calibre's own brand when known, so a
        // short/ambiguous old name (e.g. "630") only matches refs of that brand
        // and doesn't collide across other brands that reuse the string. Stays
        // universal for movement-manufacturer calibres that have no brand.
        const demoted: CalibreAliasDetail = entry.brandId
          ? { name: oldName, brandId: entry.brandId, showInExplore: false }
          : { name: oldName, showInExplore: false };
        patch.aliasDetails = [...existingDetails, demoted];
      }
    }
  }

  // Description (always-apply, top-level — not under data.*).
  if (acceptedSpecKeys.includes(CALIBRE_DESCRIPTION_KEY)) {
    const d = (result.description || '').trim();
    if (d) patch.description = d;
  }

  // Spec cells → coerce in one pass so the spec→data key remaps and type coercion
  // exactly match the panel, then emit one `data.<field>` per coerced value.
  const subset: Record<string, unknown> = {};
  for (const specKey of acceptedSpecKeys) {
    if (specKey === CALIBRE_NAME_KEY || specKey === CALIBRE_DESCRIPTION_KEY) continue;
    if (!CALIBRE_ENRICH_FIELDS[specKey]) continue;
    if (specKey in result.specs) subset[specKey] = result.specs[specKey];
  }
  const coerced = aiSpecsToCalibreData(subset) as Record<string, unknown>;
  for (const [dataKey, value] of Object.entries(coerced)) {
    patch[`data.${dataKey}`] = value;
  }

  return patch;
}
